/* Silumen · Remise sur quantité (affichage). La remise est appliquée par SMART Functions.
   Textes d'interface : clés smt.remise_qte.* et smt.common.ht / ttc, passées par Liquid
   dans le bloc JSON .smq__data (champ "i18n"). Aucun texte de langue dans ce fichier.
   Opération temporaire : champ "op" du JSON (paliers, début, fin). Pendant l'opé, l'échelle
   [data-smq-set="op"] remplace l'échelle [data-smq-set="base"], qui revient à la fin. */
(() => {
  if (customElements.get('silumen-remise-qte')) return;

  const VAT = 1.2;
  // Même lecture que le script HT/TTC du footer : 'taxToggleAll' ('true' = TTC), sinon 'tax_mode'
  const isHT = () => {
    try {
      const v = localStorage.getItem('taxToggleAll');
      if (v === 'true' || v === 'false') return v === 'false';
      return localStorage.getItem('tax_mode') === 'ht';
    } catch (e) {
      return false;
    }
  };

  // Prix : format identique au script HT/TTC du footer (et au format monétaire de la boutique).
  // Ne pas passer en Intl ici : si les deux scripts n'écrivent pas exactement la même chaîne
  // sur le prix principal, ils se réécrivent l'un l'autre en boucle.
  const num = (cents) => (Math.round(cents) / 100).toFixed(2).replace('.', ',') + ' €';

  // Remplace %1, %2... dans une phrase traduite
  const tr = (s, ...args) => args.reduce((out, v, i) => out.split('%' + (i + 1)).join(String(v)), String(s || ''));

  // Paliers 'qté:remise,qté:remise' -> [{ min, p10 }] triés
  const parseTiers = (str) => String(str || '')
    .split(',')
    .map((s) => {
      const [m, p] = s.split(':');
      return { min: parseInt(m, 10), p10: Math.round(parseFloat(String(p).replace(',', '.')) * 10) };
    })
    .filter((t) => t.min > 1 && t.p10 > 0)
    .sort((a, b) => a.min - b.min);

  // Signal panier : le thème ne publie pas d'événement fiable, on surveille les appels /cart/...
  if (!window.__smqCartHook) {
    window.__smqCartHook = true;
    const isCart = (u) => typeof u === 'string' && /\/cart\/(add|change|update|clear)(\.js)?/.test(u);
    const ping = () => setTimeout(() => document.dispatchEvent(new Event('smq:cart')), 80);
    if (window.fetch) {
      const nativeFetch = window.fetch;
      window.fetch = function () {
        const u = (arguments[0] && arguments[0].url) || arguments[0];
        return nativeFetch.apply(this, arguments).then((res) => {
          if (isCart(u)) ping();
          return res;
        });
      };
    }
    const nativeOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, u) {
      try { if (isCart(u)) this.addEventListener('load', ping); } catch (e) {}
      return nativeOpen.apply(this, arguments);
    };
  }

  class SilumenRemiseQte extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      let data;
      try {
        data = JSON.parse(this.querySelector('.smq__data').textContent);
      } catch (e) {
        return;
      }

      // Paliers de base et paliers de l'opération
      this.baseTiers = parseTiers(data.tiers);
      this.op = null;
      if (data.op && data.op.tiers) {
        const opTiers = parseTiers(data.op.tiers);
        const start = Date.parse(data.op.start);
        const end = Date.parse(data.op.end);
        if (opTiers.length && !isNaN(end)) this.op = { tiers: opTiers, start: isNaN(start) ? 0 : start, end };
      }
      this.tiers = [];
      this.set = undefined;
      this.variants = data.variants || {};
      this.multi = !!data.multi;

      // Textes de la langue courante (mêmes clés HT/TTC que le script du footer)
      this.L = data.i18n || {};
      this.lblHT = this.L.ht || this.dataset.ht || 'HT';
      this.lblTTC = this.L.ttc || this.dataset.ttc || 'TTC';
      let nf;
      try {
        nf = new Intl.NumberFormat(data.locale || document.documentElement.lang || undefined, { maximumFractionDigits: 1 });
      } catch (e) {
        nf = new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 });
      }
      this.pctLabel = (p10) => '-' + nf.format(p10 / 10) + '%';

      // form.id renverrait le <select name="id"> : on passe par l'attribut
      this.formId = this.getAttribute('form-id');
      this.form = document.getElementById(this.formId);
      if ((!this.baseTiers.length && !this.op) || !this.form) return;

      const sectionId = this.getAttribute('section-id');
      const productId = this.getAttribute('product-id');
      this.root = document.getElementById(`ProductSection-${sectionId}-${productId}`) || document;

      this.ladders = {
        base: this.querySelector('.smq__ladder[data-smq-set="base"]'),
        op: this.querySelector('.smq__ladder[data-smq-set="op"]')
      };
      this.ladder = null;
      this.opHead = this.querySelector('[data-smq-op]');
      this.nudgeEl = this.querySelector('.smq__nudge');
      this.saveEl = this.querySelector('.smq__save');
      this.bestEl = document.querySelector(`[data-smq-best="${this.formId}"]`);
      this.priceBlock = this.root.querySelector('.product-block--price');
      this.qtyInput = this.root.querySelector(`#Quantity-${sectionId}-${productId}`) || this.root.querySelector('input[name="quantity"]');
      this.atcBtn = this.form.querySelector('[data-add-to-cart]');
      this.atcText = this.form.querySelector('[data-add-to-cart-text]');
      this.idEl = this.form.querySelector('[name="id"]');
      this.variantId = this.idEl ? String(this.idEl.value) : null;
      this.cartItems = [];

      // Le thème réécrit le bloc prix après coup (changement de variante) : on réapplique le prix du palier
      if (this.priceBlock && window.MutationObserver) {
        let timer;
        this.headObs = new MutationObserver(() => {
          clearTimeout(timer);
          timer = setTimeout(() => this.render(), 60);
        });
        this.headObs.observe(this.priceBlock, { childList: true, subtree: true, characterData: true });
      }

      // Variante : on relit le champ id du formulaire après la mise à jour faite par le thème
      const syncVariant = () => {
        [60, 350].forEach((d) => setTimeout(() => {
          if (this.idEl && this.idEl.value) this.variantId = String(this.idEl.value);
          this.render();
        }, d));
      };
      this.root.addEventListener('change', (e) => { if (e.target !== this.qtyInput) syncVariant(); });
      if (this.idEl) this.idEl.addEventListener('change', syncVariant);
      ['variant:change', 'variant:changed', 'product:variant:change'].forEach((n) => document.addEventListener(n, syncVariant));

      // Quantité : les boutons +/- du thème changent la valeur sans événement fiable
      if (this.qtyInput) {
        this.qtyInput.addEventListener('input', () => this.render());
        this.qtyInput.addEventListener('change', () => this.render());
        const wrap = this.qtyInput.closest('.js-qty__wrapper');
        if (wrap) wrap.addEventListener('click', () => setTimeout(() => this.render(), 0));
      }

      // Clic sur un palier (échelle de base ou de l'opé) : on complète la quantité en tenant compte du panier
      this.addEventListener('click', (e) => {
        const cell = e.target.closest('.smq__cell');
        if (!cell || cell.disabled || !this.contains(cell)) return;
        this.setQty(Math.max(1, parseInt(cell.dataset.min, 10) - this.inCartQty()));
      });

      // Panier
      document.addEventListener('smq:cart', () => this.fetchCart());
      ['cart:updated', 'ajaxProduct:added', 'cart:change'].forEach((n) => document.addEventListener(n, () => this.fetchCart()));

      // Bascule HT/TTC (toggle du header, bandeau de choix, autre onglet)
      document.addEventListener('change', (e) => {
        if (e.target && e.target.id === 'taxToggleAll') setTimeout(() => this.render(), 0);
      });
      document.addEventListener('click', (e) => {
        if (e.target && e.target.closest && e.target.closest('#stayTTC, #goHT')) setTimeout(() => this.render(), 50);
      });
      window.addEventListener('storage', (e) => {
        if (e.key === 'taxToggleAll' || e.key === 'tax_mode') this.render();
      });

      // Début et fin de l'opé : bascule d'échelle sans recharger la page
      if (this.op) {
        [this.op.start, this.op.end].forEach((t) => {
          const d = t - Date.now();
          if (d > 0 && d < 2147483647) setTimeout(() => this.render(), d + 1000);
        });
      }

      this.render();
      this.fetchCart();
    }

    // Échelle à afficher maintenant : 'op' pendant l'opé, sinon 'base', sinon rien
    pickSet() {
      const now = Date.now();
      if (this.op && this.ladders.op && now >= this.op.start && now < this.op.end) return 'op';
      if (this.baseTiers.length && this.ladders.base) return 'base';
      return null;
    }

    applySet(set) {
      if (set === this.set) return;
      this.set = set;
      this.tiers = set === 'op' ? this.op.tiers : set === 'base' ? this.baseTiers : [];
      this.ladder = set ? this.ladders[set] : null;
      Object.keys(this.ladders).forEach((k) => {
        if (this.ladders[k]) this.ladders[k].hidden = k !== set;
      });
      if (this.opHead) this.opHead.hidden = set !== 'op';
    }

    fetchCart() {
      fetch('/cart.js', { credentials: 'same-origin' })
        .then((r) => r.json())
        .then((cart) => {
          this.cartItems = (cart && cart.items) || [];
          this.render();
        })
        .catch(() => {});
    }

    inCartQty() {
      return this.cartItems
        .filter((it) => String(it.variant_id) === this.variantId)
        .reduce((sum, it) => sum + it.quantity, 0);
    }

    setQty(n) {
      if (!this.qtyInput) return;
      this.qtyInput.value = n;
      this.qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      this.render();
    }

    render() {
      const v = this.variants[this.variantId];
      if (!v || !v.a) return this.hideAll();

      const L = this.L;
      const price = v.p;
      const ht = isHT();
      const suffix = ht ? this.lblHT : this.lblTTC;
      const net = (c) => (ht ? c / VAT : c);
      const show = (c) => `${num(net(c))} ${suffix}`;
      const unitOf = (p10) => price - Math.floor((price * p10) / 1000); // troncature SMART Functions
      const stock = v.c ? Math.max(0, v.q) : Infinity;

      // Échelle du moment (opé ou base) ; aucune : on masque tout
      this.applySet(this.pickSet());
      if (!this.tiers.length || !this.ladder) return this.hideAll(price, show);

      // Stock sous le premier palier : aucune remise possible, on masque tout
      if (stock < this.tiers[0].min) return this.hideAll(price, show);
      this.hidden = false;

      const inCart = this.inCartQty();
      const qty = Math.max(1, parseInt(this.qtyInput ? this.qtyInput.value : 1, 10) || 1);
      const room = Math.max(0, stock - inCart); // pièces encore ajoutables sur cette variante
      const qtyOk = Math.min(qty, room);        // quantité réellement commandable
      const eff = inCart + qtyOk;

      let i = -1;
      this.tiers.forEach((t, k) => { if (eff >= t.min) i = k; });
      const p10 = i >= 0 ? this.tiers[i].p10 : 0;
      let next = this.tiers[i + 1];
      if (next && next.min > stock) next = null;

      // Ligne sous le prix
      if (this.bestEl) {
        const reachable = this.tiers.filter((t) => t.min <= stock);
        const best = reachable[reachable.length - 1];
        this.bestEl.hidden = !best || i >= 0;
        if (best) {
          const pctEl = this.bestEl.querySelector('.smq-best__pct');
          if (pctEl) pctEl.textContent = this.pctLabel(best.p10);
          const bestPriceEl = this.bestEl.querySelector('.smq-best__price');
          if (bestPriceEl) bestPriceEl.textContent = show(unitOf(best.p10));
        }
      }

      // Prix principal
      this.updateHead(i >= 0 ? { unit: unitOf(p10), lbl: this.pctLabel(p10) } : null, price, show);
      if (this.headObs) this.headObs.takeRecords(); // nos propres écritures ne relancent pas la surveillance

      // Échelle
      const rows = [{ min: 1, p10: 0 }].concat(this.tiers);
      const cells = Array.from(this.ladder.querySelectorAll('.smq__cell'));
      if (cells.length !== rows.length) return;
      const firstMin = this.tiers[0].min;
      rows.forEach((r, k) => {
        const cell = cells[k];
        const nextMin = rows[k + 1] ? rows[k + 1].min : Infinity;
        const passed = inCart + 1 >= nextMin; // palier déjà dépassé grâce au panier
        const noStock = r.min > stock;
        const active = k === i + 1;
        cell.disabled = passed || noStock;
        cell.classList.toggle('is-active', active);
        cell.setAttribute('aria-pressed', active ? 'true' : 'false');
        cell.querySelector('.smq__q').textContent = k === 0 ? (firstMin > 2 ? tr(L.range, firstMin - 1) : '1') : `${r.min}+`;
        cell.querySelector('.smq__p').textContent = num(net(unitOf(r.p10)));
        cell.querySelector('.smq__u').textContent = suffix;
        cell.querySelector('.smq__s').textContent = passed ? tr(L.passed) : noStock ? tr(L.inStock, stock) : k === 0 ? tr(L.base) : this.pctLabel(r.p10);
      });
      // Prix à 4 chiffres ou plus : l'échelle passe en taille réduite (mobile) pour garder TTC / HT à droite
      this.ladder.classList.toggle('is-long', cells.some((c) => /\d{4},/.test(c.querySelector('.smq__p').textContent)));
      // Message (les paliers commencent toujours à 2 pièces ou plus : phrases au pluriel)
      let cartTier = -1;
      this.tiers.forEach((t, k) => { if (inCart >= t.min) cartTier = k; });
      let nudge = '';
      if (inCart && i > cartTier) {
        nudge = tr(L.reached, eff, inCart, show(unitOf(p10)));
      } else if (next) {
        const target = next.min - inCart;
        if (inCart) nudge = tr(L.addMore, target, next.min, inCart, show(unitOf(next.p10)));
        else if (i < 0) nudge = tr(this.multi ? L.fromSame : L.from, next.min, this.pctLabel(next.p10));
        else nudge = tr(L.goTo, target, show(unitOf(next.p10)));
      }
      this.nudgeEl.textContent = nudge;
      this.saveEl.textContent = i >= 0 ? tr(L.save, show(eff * (price - unitOf(p10)))) : '';

      if (room === 0) this.restoreAtc();
      else this.updateAtc(show(qtyOk * unitOf(p10)));
    }

    hideAll(price, show) {
      this.hidden = true;
      if (this.bestEl) this.bestEl.hidden = true;
      if (price !== undefined) this.updateHead(null, price, show);
      if (this.headObs) this.headObs.takeRecords();
      this.restoreAtc();
    }

    updateHead(tier, base, show) {
      const block = this.priceBlock;
      if (!block) return;
      const priceEl = block.querySelector('[data-product-price]');
      if (!priceEl) return;
      const target = priceEl.querySelector('[aria-hidden="true"]:not(.visually-hidden)') || priceEl;
      block.querySelectorAll('.smq-head, .smq-head__tag').forEach((el) => el.remove());

      // Même format et même attribut que le script HT/TTC du footer
      const unit = tier ? tier.unit : base;
      target.setAttribute('data-ttc-num', String(unit / 100));
      const txt = show(unit);
      if (target.textContent !== txt) target.textContent = txt;
      if (!tier) return;

      const wrap = block.querySelector('[data-product-price-wrap]');
      const hasCompare = !!wrap && !wrap.classList.contains('hide') && wrap.textContent.trim() !== '';
      let anchor = priceEl;
      if (!hasCompare) {
        const was = document.createElement('s');
        was.className = 'smq-head';
        was.textContent = show(base);
        priceEl.insertAdjacentElement('afterend', was);
        anchor = was;
      }
      const tag = document.createElement('span');
      tag.className = 'smq-head__tag';
      tag.textContent = hasCompare ? tr(this.L.volume, tier.lbl) : tier.lbl;
      const label = block.querySelector('.label-reduction-highlight-product');
      (label || (hasCompare ? wrap : anchor)).insertAdjacentElement('afterend', tag);
    }

    updateAtc(totalLabel) {
      if (!this.atcText || (this.atcBtn && this.atcBtn.disabled)) return;
      this.atcText.textContent = `${this.atcText.dataset.defaultText || ''} · ${totalLabel}`;
    }

    restoreAtc() {
      if (!this.atcText || (this.atcBtn && this.atcBtn.disabled)) return;
      this.atcText.textContent = this.atcText.dataset.defaultText || '';
    }
  }

  customElements.define('silumen-remise-qte', SilumenRemiseQte);
})();